import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { Sheet, SheetContent } from '../ui/sheet';

import { Toaster } from '../ui/toaster';

const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const prevPathRef = useRef(location.pathname);

  // Handle responsive detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      setMobileMenuOpen(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  const handleOpenChange = useCallback((open) => {
    setMobileMenuOpen(open);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800 dark:border-slate-200" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Mobile Sidebar - Using Sheet for touch gestures */}
      {isMobile && (
        <Sheet open={mobileMenuOpen} onOpenChange={handleOpenChange}>
          <SheetContent side="left" className="p-0 w-[280px] border-r-0" hideCloseButton>
            <Sidebar 
              collapsed={false} 
              setCollapsed={setCollapsed}
              onClose={() => setMobileMenuOpen(false)}
              isMobile={true}
            />
          </SheetContent>
        </Sheet>
      )}
      
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="fixed inset-y-0 left-0 z-50">
          <Sidebar 
            collapsed={collapsed} 
            setCollapsed={setCollapsed}
            onClose={() => {}}
            isMobile={false}
          />
        </div>
      )}
      
      {/* Header */}
      <Header 
        collapsed={collapsed} 
        isMobile={isMobile}
        onMenuClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      />
      
      {/* Main Content */}
      <main
        className={cn(
          'pt-16 min-h-screen transition-all duration-300',
          isMobile ? 'ml-0' : (collapsed ? 'ml-[70px]' : 'ml-[240px]')
        )}
      >
        <div className="p-3 sm:p-4 lg:p-6">
          <Outlet />
        </div>

      <Toaster />

      </main>
    </div>
  );
};

export default MainLayout;
